import { useState, useMemo } from 'react';

export type SortDirection = 'asc' | 'desc' | null;

interface UseTableSortProps<T> {
  data: T[];
  defaultSortColumn?: keyof T | string;
  defaultSortDirection?: SortDirection;
}

interface UseTableSortReturn<T> {
  sortedData: T[];
  sortColumn: keyof T | string | null;
  sortDirection: SortDirection;
  handleSort: (column: { key: keyof T | string; sortable?: boolean }) => void;
  getSortAriaSort: (column: {
    key: keyof T | string;
    sortable?: boolean;
  }) => 'none' | 'ascending' | 'descending' | undefined;
}

/**
 * Custom hook for table sorting functionality
 * Handles sorting logic, state management, and data transformation
 */
export const useTableSort = <T extends Record<string, unknown>>({
  data,
  defaultSortColumn,
  defaultSortDirection = 'asc',
}: UseTableSortProps<T>): UseTableSortReturn<T> => {
  const [sortColumn, setSortColumn] = useState<keyof T | string | null>(
    defaultSortColumn || null
  );
  const [sortDirection, setSortDirection] =
    useState<SortDirection>(defaultSortDirection);

  const sortedData = useMemo(() => {
    if (!sortColumn || !sortDirection) return data;

    return [...data].sort((a, b) => {
      let aValue = a[sortColumn as keyof T];
      let bValue = b[sortColumn as keyof T];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'object' && aValue !== null && 'label' in aValue) {
        aValue = (aValue as { label: string }).label as unknown as T[keyof T];
      }
      if (typeof bValue === 'object' && bValue !== null && 'label' in bValue) {
        bValue = (bValue as { label: string }).label as unknown as T[keyof T];
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortDirection === 'asc' ? comparison : -comparison;
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Check if values are Date objects using Object.prototype.toString
      const isADate =
        Object.prototype.toString.call(aValue) === '[object Date]';
      const isBDate =
        Object.prototype.toString.call(bValue) === '[object Date]';

      if (isADate && isBDate) {
        const comparison =
          (aValue as Date).getTime() - (bValue as Date).getTime();
        return sortDirection === 'asc' ? comparison : -comparison;
      }

      // Check if values are date-like strings
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const aDate = new Date(aValue);
        const bDate = new Date(bValue);
        if (
          !isNaN(aDate.getTime()) &&
          !isNaN(bDate.getTime()) &&
          aValue.match(/^\d{4}-\d{2}-\d{2}/) &&
          bValue.match(/^\d{4}-\d{2}-\d{2}/)
        ) {
          const comparison = aDate.getTime() - bDate.getTime();
          return sortDirection === 'asc' ? comparison : -comparison;
        }
      }

      // Fallback to string comparison
      const aStr = String(aValue);
      const bStr = String(bValue);
      const comparison = aStr.localeCompare(bStr);
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [data, sortColumn, sortDirection]);

  const handleSort = (column: {
    key: keyof T | string;
    sortable?: boolean;
  }) => {
    if (!column.sortable) return;

    // Don't allow sorting on virtual columns (columns that don't exist in the data)
    if (data.length > 0 && !(column.key in data[0])) {
      return;
    }

    if (sortColumn === column.key) {
      // Toggle between asc and desc only (2-state)
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column.key);
      setSortDirection('asc');
    }
  };

  const getSortAriaSort = (column: {
    key: keyof T | string;
    sortable?: boolean;
  }): 'none' | 'ascending' | 'descending' | undefined => {
    if (!column.sortable) return undefined;
    if (sortColumn !== column.key) return 'none';
    return sortDirection === 'asc' ? 'ascending' : 'descending';
  };

  return {
    sortedData,
    sortColumn,
    sortDirection,
    handleSort,
    getSortAriaSort,
  };
};

export default useTableSort;
